import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BeatShell, OptionList } from "@/components/nutrition/NutritionParts";
import { optPts, beatMax, ICON_BY_TYPE } from "../beatUtils";
import { Briefing } from "./Shared";
import { Timer } from "lucide-react";

export function ChoiceBeat({ beat, eyebrow, showBriefing, c, accent, onDone, timeLimit }) {
  const [idx, setIdx] = useState(null);
  const [res, setRes] = useState(null);
  const [left, setLeft] = useState(timeLimit || 0);
  const checked = !!res;
  const Icon = beat.icon || ICON_BY_TYPE.choice;
  const idxRef = useRef(idx);
  idxRef.current = idx;

  const grade = (chosen) =>
    setRes({
      earned: chosen == null ? 0 : optPts(beat.options[chosen].score, beat.full ?? 20),
      max: beatMax(beat),
      detail: beat.detail || "",
      key: beat.key,
      label: beat.label,
      timedOut: chosen == null && !!timeLimit,
    });

  // countdown for time-pressured beats; auto-locks the current pick on expiry
  useEffect(() => {
    if (!timeLimit || checked) return undefined;
    if (left <= 0) {
      grade(idxRef.current);
      return undefined;
    }
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [left, checked, timeLimit]);

  const onPrimary = () => {
    if (!checked) {
      if (idx == null) return toast.error("Choose an option.");
      grade(idx);
    } else onDone(res);
  };

  const pct = timeLimit ? Math.max(0, (left / timeLimit) * 100) : 0;
  const tone = left <= 5 ? "#ef4444" : left <= 10 ? "#f59e0b" : accent || "#0047AB";

  return (
    <BeatShell Icon={Icon} eyebrow={eyebrow} title={beat.title} subtitle={beat.prompt}
      primaryLabel={checked ? beat.last ? "See debrief →" : "Continue →" : "Check answer"} onPrimary={onPrimary} primaryTestId={`${beat.key}-primary-btn`} accent={accent}>
      {showBriefing && <Briefing c={c} />}
      {timeLimit && !checked && (
        <div data-testid="beat-timer">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1.5" style={{ color: tone }}>
              <Timer className="w-3.5 h-3.5" /> Respond quickly
            </span>
            <span className="text-sm font-mono-clinical font-bold" style={{ color: tone }}>{left}s</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-1">
            <div className="h-full rounded-full transition-all duration-1000 ease-linear" style={{ width: `${pct}%`, background: tone }} />
          </div>
        </div>
      )}
      {checked && res.timedOut && (
        <div className="rounded-md border border-rose-300 bg-rose-50 text-rose-700 text-sm font-semibold px-3 py-2" data-testid="beat-timeout">
          Time ran out — in a crisis, a delayed decision costs the patient. Review the correct answer below.
        </div>
      )}
      <OptionList options={beat.options} value={idx} onPick={(i) => !checked && setIdx(i)} revealed={checked} testidPrefix={`${beat.key}-option`} />
    </BeatShell>
  );
}
